import { createWebHistory, createRouter } from "vue-router";
import { isLoggedIn, isAdmin, isPlanExpired } from "./utils/auth";
import { notify } from "./utils/notify";
import { POST_BID_ENABLED, POST_BID_ROUTES, CHECKLIST_ENABLED, CHECKLIST_ROUTES } from "./config/features";


import HomePage from './components/Home.vue'
import LoginPage from './components/UserLogin.vue'
import UserRegistration from './components/UserRegistration.vue'
import ForgotPassword from './components/ForgotPassword.vue'
import BidListing from './components/BidListing.vue'
import PdfView from './components/pdfView.vue'
import RiskRegisterView from './components/RiskRegisterView.vue'
import ObligationsView from './components/ObligationsView.vue'
import FormSubmission from './components/FormSubmission.vue'
import AcceptanceCriteria from './components/AcceptanceCriteria.vue'
import EligibilityCriteria from './components/EligibilityCriteria.vue'
import AccountPage from './components/Account.vue'
import CompanyAdmin from './components/CompanyAdmin.vue'
import NotificationCenter from './components/NotificationCenter.vue'
import ActiveProjects from './components/ActiveProjects.vue'
import CompletedProjects from './components/CompletedProjects.vue'
import ProjectBoard from './components/ProjectBoard.vue'
import BidDetails from './components/BidDetails.vue'
import BidChecklist from './components/BidChecklist.vue'
import Deliverables from './components/Deliverables.vue'
import CompanyKnowledgeBase from './components/CompanyKnowledgeBase.vue'
import StandardDocuments from './components/StandardDocuments.vue'
import NotFound from './components/NotFound.vue'
import TenderList from './components/TenderList.vue'
import TenderDetail from './components/TenderDetail.vue'
import Bookmarks from './components/Bookmarks.vue'
import AuthCallback from './components/AuthCallback.vue'
import PlanExpired from './components/PlanExpired.vue'

// Marketing (BidSnap AI) public site — merged in. Rendered inside its own
// MarketingLayout (own navbar/footer + `.marketing-app` scoped styling) so it
// never touches the bid_ui app shell or styles.
import MarketingLayout from './marketing/MarketingLayout.vue'

const marketingRoute = {
    path: "/",
    component: MarketingLayout,
    // Logged-in users hitting the landing root go straight to the app dashboard.
    beforeEnter: (to) => {
        // Logged-in users can still see the marketing site at /
    },
    children: [
        { path: "", name: "marketing-home", component: () => import('./marketing/views/HomeView.vue'), meta: { layout: 'marketing', public: true, title: 'AI Bid Intelligence Platform' } },
        { path: "tender-insights", name: "marketing-tender-insights", component: () => import('./marketing/views/TenderInsightsView.vue'), meta: { layout: 'marketing', public: true, title: 'Tender Insights & Analysis' } },
        { path: "bid-intelligence", name: "marketing-bid-intelligence", component: () => import('./marketing/views/BidIntelligenceView.vue'), meta: { layout: 'marketing', public: true, title: 'Win Rate Bid Intelligence' } },
        { path: "timeline", name: "marketing-timeline", component: () => import('./marketing/views/TimelineView.vue'), meta: { layout: 'marketing', public: true, title: 'Timeline & Notifications' } },
        { path: "email-intelligence", name: "marketing-email-intelligence", component: () => import('./marketing/views/EmailIntelligenceView.vue'), meta: { layout: 'marketing', public: true, title: 'Email Intelligence' } },
        { path: "claims", name: "marketing-claims", component: () => import('./marketing/views/ClaimsView.vue'), meta: { layout: 'marketing', public: true, title: 'Claims Management' } },
        { path: "documents", name: "marketing-documents", component: () => import('./marketing/views/DocumentsView.vue'), meta: { layout: 'marketing', public: true, title: 'Contract & Document Management' } },
        { path: "chat-assistant", name: "marketing-chat-assistant", component: () => import('./marketing/views/ChatAssistantView.vue'), meta: { layout: 'marketing', public: true, title: 'AI Contract Chat Assistant' } },
        { path: "contact", name: "marketing-contact", component: () => import('./marketing/views/ContactView.vue'), meta: { layout: 'marketing', public: true, title: 'Request a Demo' } },
        { path: "api-docs", name: "marketing-api-docs", component: () => import('./marketing/views/ApiDocsView.vue'), meta: { layout: 'marketing', public: true, title: 'Tender API Documentation' } },
    ],
};

const routes = [
    marketingRoute,
    {
        name: 'Bookmarks',
        path: "/bookmarks",
        component: Bookmarks
    },
    {
        name: 'Tenders',
        path: "/tender-search",
        component: TenderList
    },
    {
        name: 'TenderDetail',
        path: "/tender-search/:id",
        component: TenderDetail
    },
    {
        name: 'Dashboard',
        path: "/tender-search",
        component: TenderList
    },
    {
        name: 'Home',
        path: "/tender-synopsis",
        component: HomePage
    },
    {
        name: 'LoginPage',
        path: "/login",
        component: LoginPage
    },
    {
        name: 'AuthCallback',
        path: "/auth/callback",
        component: AuthCallback
    },
    {
        name: 'UserRegistration',
        path: "/registration",
        component: UserRegistration
    },
    {
        name: 'ForgotPassword',
        path: "/forgotpassword",
        component: ForgotPassword
    },
    {
        name: 'BidListing',
        path: "/Bid",
        component: BidListing
    },
    {
        name: 'BidDetails',
        path: "/bid-details/:pdf_id",
        component: BidDetails
    },
    {
        name: 'BidChecklist',
        path: "/bid-checklist/:pdf_id",
        component: BidChecklist
    },
    {
        name: 'Deliverables',
        path: "/deliverables/:pdf_id",
        component: Deliverables
    },
    {
        name: 'PdfView',
        path: "/pdf-view/:pdf_id",

        component: PdfView
    },
    {
        name: 'RiskRegisterView',
        path: "/risk-register/:pdf_id",
        component: RiskRegisterView
    },
    {
        name: 'ObligationsView',
        path: "/obligations/:pdf_id",
        component: ObligationsView
    },
    {
        name: 'FormSubmission',
        path: "/form-submission/:pdf_id",
        component: FormSubmission
    },
    {
        name: 'AcceptanceCriteria',
        path: "/acceptance-criteria/:pdf_id",
        component: AcceptanceCriteria
    },
    {
        name: 'EligibilityCriteria',
        path: "/eligibility-criteria/:pdf_id",
        component: EligibilityCriteria
    },
    {
        name: 'Account',
        path: "/account",
        component: AccountPage
    },
    {
        name: 'CompanyAdmin',
        path: "/company-admin",
        component: CompanyAdmin
    },
    {
        name: 'CompanyKnowledgeBase',
        path: "/company-knowledge-base",
        component: CompanyKnowledgeBase
    },
    {
        // Shared Standard Documents library. Searchable by every logged-in user;
        // uploading is gated to Super Admins inside the component (and the API).
        name: 'StandardDocuments',
        path: "/standard-documents",
        component: StandardDocuments
    },
    {
        name: 'NotificationCenter',
        path: "/notifications",
        component: NotificationCenter
    },
    {
        name: 'ActiveProjects',
        path: "/active-projects",
        component: ActiveProjects
    },
    {
        name: 'CompletedProjects',
        path: "/completed-projects",
        component: CompletedProjects
    },
    {
        name: 'ProjectBoard',
        path: "/project-board/:pdf_id",
        component: ProjectBoard
    },
    {
        name: 'Analytics',
        path: "/project/:id/analytics",
        component: () => import('./components/Analytics.vue')
    },
    {
        name: 'PlanExpired',
        path: "/plan-expired",
        component: PlanExpired
    },
    {
        name: 'not-found',
        path: "/:pathMatch(.*)*",
        component: NotFound
    }
];

const router = createRouter({ history: createWebHistory(), routes });

// Routes reachable without login. Everything else redirects to the login
// page — this is the real gate; the sidebar blur is only the visual hint.
const PUBLIC_ROUTES = new Set([
    "Tenders",
    "TenderDetail",
    "LoginPage",
    "AuthCallback",
    "UserRegistration",
    "ForgotPassword",
    "not-found",
]);

// Routes only an admin may open, with the name shown when a viewer is turned
// away. Viewers (role "user") go to the dashboard with a note saying why.
const ADMIN_ONLY_ROUTES = new Map([
    ["CompanyAdmin", "Admin Panel"],
    ["CompanyKnowledgeBase", "Company Knowledge Base"],
    ["Home", "Tender Synopsis"],   // tender upload
]);

// Routes still reachable once a company's plan has expired — just enough to
// log out or land on the expired-plan page itself. Everything else redirects
// there. The backend is the real gate (every API call 403s once expired);
// this only stops the user from sitting on a dead page with nothing working.
const PLAN_EXPIRED_ALLOWED_ROUTES = new Set([
    "PlanExpired",
    "LoginPage",
    "AuthCallback",
    "not-found",
]);

router.beforeEach((to) => {
    // `meta.public` covers the merged marketing pages; PUBLIC_ROUTES covers the
    // app's own public pages (tenders, login, etc.).
    if (!isLoggedIn() && !to.meta?.public && !PUBLIC_ROUTES.has(to.name)) {
        return { name: "LoginPage" };
    }
    // Admin-only areas: a logged-in viewer goes to the dashboard and is told
    // why, instead of silently landing somewhere else.
    if (isLoggedIn() && !isAdmin() && ADMIN_ONLY_ROUTES.has(to.name)) {
        try {
            notify.warning(`${ADMIN_ONLY_ROUTES.get(to.name)} is for admins only.`);
        } catch (e) { /* toast plugin not ready on the very first navigation */ }
        return { name: "Dashboard" };
    }
    // Post-bid pages are switched off for now (config/features.js); their
    // routes stay registered so turning the flag on brings them straight back.
    if (!POST_BID_ENABLED && POST_BID_ROUTES.has(to.name)) {
        return { name: "Dashboard" };
    }
    // Same for the Features Checklist.
    if (!CHECKLIST_ENABLED && CHECKLIST_ROUTES.has(to.name)) {
        return { name: "Dashboard" };
    }
    if (isLoggedIn() && isPlanExpired() && !PLAN_EXPIRED_ALLOWED_ROUTES.has(to.name)) {
        return { name: "PlanExpired" };
    }
});

// Keep the document title in sync with the marketing pages' meta.
router.afterEach((to) => {
    if (to.meta?.layout === 'marketing') {
        const base = 'BidSnap AI';
        document.title = to.meta.title ? `${to.meta.title} · ${base}` : base;
    }
});

export default router;
